/**
 * Orders mediaResponses based on their appearance in viewpoint responses
 * @param mediaResponses - Array of media responses to order
 * @param responses - The viewpoint response JSON containing sections and questions
 * @returns Ordered array of media responses
 */
export const orderMediaResponsesByViewpointResponses = (mediaResponses: any[], responses: any): any[] => {
    if (!mediaResponses || !responses || !responses.sections) {
        return mediaResponses || [];
    }

    return mediaResponses.sort((a: any, b: any) => {
        // Find the sections and questions that match these media responses
        const findQuestionIndex = (response: any) => {
            for (const section of responses.sections) {
                const questionIndex = section.questions.findIndex(
                    (q: any) => {
                        // Check both answers[0] and answers[1] for UUID matches
                        return (q.answers[0]?.includes(response.uuid) || 
                                q.answers[1]?.includes(response.uuid) ||
                                q.answers[0]?.match(new RegExp(`^(video|audio|text)UUID:${response.uuid}$`)) ||
                                q.answers[1]?.match(new RegExp(`^(video|audio|text)UUID:${response.uuid}$`)));
                    }
                );
                if (questionIndex !== -1) {
                    return questionIndex;
                }
            }
            return Infinity; // Put items without matching questions at the end
        };
        
        return findQuestionIndex(a) - findQuestionIndex(b);
    });
};

/**
 * Orders mediaResponses across multiple viewpoints, showing them in the sequence they appear 
 * from the start of the first viewpoint to the end of the last viewpoint
 * @param mediaResponses - Array of media responses to order
 * @param viewpoints - Array of viewpoints containing response_json and media_responses
 * @returns Ordered array of media responses
 */
export const orderMediaResponsesByMultipleViewpoints = (mediaResponses: any[], viewpoints: any[]): any[] => {
    if (!mediaResponses || !viewpoints || viewpoints.length === 0) {
        return mediaResponses || [];
    }

    // Create a map to store the position of each media response
    const mediaResponsePositions = new Map<string, number>();

    // Process each viewpoint in order
    viewpoints.forEach((viewpoint, viewpointIndex) => {
        const responses = viewpoint.response_json;
        if (!responses || !responses.sections) {
            return;
        }

        // Calculate base position for this viewpoint (each viewpoint gets a large offset)
        const basePosition = viewpointIndex * 10000;

        // Process each section and question in the viewpoint
        responses.sections.forEach((section: any, sectionIndex: number) => {
            section.questions.forEach((question: any, questionIndex: number) => {
                // Check if this question contains any of our media responses
                const checkForMediaResponse = (answer: any) => {
                    if (!answer) return;
                    
                    // Check for UUID matches in various formats
                    const uuidMatch = answer.match(/^(video|audio|text)UUID:(.*)$/);
                    if (uuidMatch) {
                        const uuid = uuidMatch[2];
                        const mediaResponse = mediaResponses.find(mr => mr.uuid === uuid);
                        if (mediaResponse) {
                            // Calculate position: viewpoint * 10000 + section * 1000 + question
                            const position = basePosition + (sectionIndex * 1000) + questionIndex;
                            mediaResponsePositions.set(uuid, position);
                        }
                    }
                };

                // Check both answers[0] and answers[1]
                checkForMediaResponse(question.answers[0]);
                checkForMediaResponse(question.answers[1]);
            });
        });
    });

    // Sort media responses based on their calculated positions
    return mediaResponses.sort((a: any, b: any) => {
        const positionA = mediaResponsePositions.get(a.uuid) ?? Infinity;
        const positionB = mediaResponsePositions.get(b.uuid) ?? Infinity;
        return positionA - positionB;
    });
}; 